
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Influencer, SupabaseInfluencer } from "@/types/influencer";

export const useInfluencerData = (influencerId: string | undefined) => {
  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchInfluencer = async () => {
      if (!influencerId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Try to fetch from Supabase first
        const { data, error } = await supabase
          .from('influencers')
          .select('*')
          .eq('id', influencerId)
          .maybeSingle();
        
        if (error || !data) {
          console.error("Error fetching influencer:", error);
          // Fall back to mock data if not found in database
          const mockInfluencers = [
            {
              id: "1",
              name: "N/A",
              category: "N/A",
              followers: "N/A",
              price: 0,
              availability: "N/A",
              image: "/images/434d43cf-a521-43d5-8239-5f00c9aaad1a.png",
              rating: 0,
              featured: true,
              description: "N/A",
              expertise: ["N/A"],
              socialLinks: {
                instagram: "N/A",
                youtube: "N/A",
                tiktok: "N/A"
              },
              contactInfo: {
                email: "N/A",
                phone: "N/A"
              }
            },
            {
              id: "2",
              name: "N/A",
              category: "N/A",
              followers: "N/A",
              price: 0,
              availability: "N/A",
              image: "/images/cd969d7d-76eb-436d-a0bf-43509ea63a91.png",
              rating: 0,
              featured: true,
              description: "N/A",
              expertise: ["N/A"],
              socialLinks: {
                instagram: "N/A",
                youtube: "N/A"
              },
              contactInfo: {
                email: "N/A"
              }
            },
            {
              id: "3",
              name: "N/A",
              category: "N/A",
              followers: "N/A",
              price: 0,
              availability: "N/A",
              image: "/images/b13f53fa-3265-4bf3-89d2-d328e3597c4a.png",
              rating: 0,
              featured: false,
              description: "N/A",
              expertise: ["N/A"],
              socialLinks: {
                youtube: "N/A",
                tiktok: "N/A"
              },
              contactInfo: {
                email: "N/A",
                phone: "N/A"
              }
            },
            {
              id: "4",
              name: "N/A",
              category: "N/A",
              followers: "N/A",
              price: 0,
              availability: "N/A",
              image: "/images/5b702a1b-0694-48a1-9c09-9a077bc3c36b.png",
              rating: 0,
              featured: true,
              description: "N/A",
              expertise: ["N/A"],
              socialLinks: {
                instagram: "N/A",
                youtube: "N/A",
                tiktok: "N/A"
              },
              contactInfo: {
                email: "N/A"
              }
            },
            {
              id: "5",
              name: "N/A",
              category: "N/A",
              followers: "N/A",
              price: 0,
              availability: "N/A",
              image: "/images/dc5acc6c-9d2a-4715-9c31-e8c3e7c96ab0.png",
              rating: 0,
              featured: false,
              description: "N/A",
              expertise: ["N/A"],
              socialLinks: {
                instagram: "N/A",
                youtube: "N/A"
              },
              contactInfo: {
                email: "N/A",
                phone: "N/A"
              }
            },
            {
              id: "6",
              name: "N/A",
              category: "N/A",
              followers: "N/A",
              price: 0,
              availability: "N/A",
              image: "/images/5ad78f82-421d-4e5a-9a7e-6407b128307d.png",
              rating: 0,
              featured: true,
              description: "N/A",
              expertise: ["N/A"],
              socialLinks: {
                instagram: "N/A",
                tiktok: "N/A"
              },
              contactInfo: {
                email: "N/A"
              }
            },
          ];
          
          const mockInfluencer = mockInfluencers.find(inf => inf.id === influencerId);
          if (mockInfluencer) {
            setInfluencer(mockInfluencer);
          } else {
            toast({
              title: "Influencer not found",
              description: "The influencer you're looking for could not be found.",
              variant: "destructive"
            });
          }
        } else {
          // If data was found in Supabase, adapt it to our interface
          const dbData = data as any;
          
          setInfluencer({
            id: dbData.id,
            name: dbData.name,
            category: dbData.category,
            followers: dbData.followers || "0",
            price: dbData.price || 0,
            availability: dbData.availability || "Available",
            image: dbData.image || "/placeholder.svg",
            rating: dbData.rating || 0,
            featured: dbData.featured || false,
            description: dbData.description || "",
            expertise: dbData.expertise || [],
            packages: dbData.packages || [],
            socialLinks: dbData.social_links ? {
              instagram: dbData.social_links.instagram,
              instagram_followers: dbData.social_links.instagram_followers,
              youtube: dbData.social_links.youtube,
              youtube_followers: dbData.social_links.youtube_followers,
              tiktok: dbData.social_links.tiktok,
              tiktok_followers: dbData.social_links.tiktok_followers,
              facebook: dbData.social_links.facebook,
              facebook_followers: dbData.social_links.facebook_followers,
              twitter: dbData.social_links.twitter,
              twitter_followers: dbData.social_links.twitter_followers,
            } : undefined,
            contactInfo: dbData.contact_info ? {
              email: dbData.contact_info.email,
              phone: dbData.contact_info.phone,
            } : undefined
          } as Influencer);
        }
      } catch (error) {
        console.error("Error in fetchInfluencer:", error);
        toast({
          title: "Error",
          description: "Failed to load influencer details. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInfluencer();
  }, [influencerId, toast]);

  return { influencer, isLoading };
};
